import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, 
  Download, 
  Loader2, 
  FileText, 
  Image as ImageIcon,
  File,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut
} from 'lucide-react';
import { toast } from 'sonner';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DocumentViewerPage = () => {
  const { caseId, documentId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [loadingContent, setLoadingContent] = useState(false);
  
  // PDF viewer state
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.2);
  const [pdfError, setPdfError] = useState(null);

  useEffect(() => {
    if (caseId && documentId) {
      fetchDocument();
    }
    
    // Cleanup blob URL on unmount
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [caseId, documentId]);

  const fetchDocument = async () => {
    setLoading(true);
    setError(null);
    try {
      // Get view URL with metadata
      const response = await axios.get(`${API}/cases/${caseId}/documents/${documentId}/view`);
      setDocument(response.data);
      
      // Now fetch the actual file content as blob
      if (response.data.file_url) {
        await fetchFileContent(response.data.file_url, response.data.mime_type);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to load document';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const fetchFileContent = async (fileUrl, mimeType) => {
    setLoadingContent(true);
    try {
      // Build full URL for the file
      const fullUrl = fileUrl.startsWith('http') 
        ? fileUrl 
        : `${BACKEND_URL}${fileUrl}`;
      
      console.log('Fetching document from:', fullUrl);
      
      // Fetch the file as blob with auth header
      const response = await axios.get(fullUrl, {
        responseType: 'blob'
      });
      
      console.log('Received blob, size:', response.data.size, 'type:', response.data.type);
      
      // Determine the correct mime type - prioritize what we know, fallback to response
      const detectedType = mimeType || response.data.type || response.headers['content-type'] || 'application/pdf';
      
      // Create a new blob with explicit mime type
      const blob = new Blob([response.data], { type: detectedType });
      console.log('Created blob with type:', detectedType, 'size:', blob.size);
      
      // Create blob URL for display
      const url = URL.createObjectURL(blob);
      console.log('Created blob URL:', url.substring(0, 50) + '...');
      setBlobUrl(url);
    } catch (err) {
      console.error('Failed to load file content:', err);
      toast.error('Unable to load document preview');
    } finally {
      setLoadingContent(false);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await axios.get(`${API}/cases/${caseId}/documents/${documentId}/download`);
      
      if (response.data.file_url) {
        // Fetch as blob and trigger download
        const fullUrl = response.data.file_url.startsWith('http') 
          ? response.data.file_url 
          : `${BACKEND_URL}${response.data.file_url}`;
        
        const fileResponse = await axios.get(fullUrl, { responseType: 'blob' });
        const blob = new Blob([fileResponse.data]);
        const url = URL.createObjectURL(blob);
        
        const link = window.document.createElement('a');
        link.href = url;
        link.download = document?.document_name || 'document';
        window.document.body.appendChild(link);
        link.click();
        window.document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        toast.success(`Downloading: ${document?.document_name}`);
      }
    } catch (err) {
      toast.error('Failed to download document');
    }
  };

  const handleOpenInNewTab = async () => {
    if (blobUrl) {
      window.open(blobUrl, '_blank');
    } else if (document?.file_url) {
      // Fetch and open if we don't have blob yet
      try {
        const fullUrl = document.file_url.startsWith('http') 
          ? document.file_url 
          : `${BACKEND_URL}${document.file_url}`;
        
        const response = await axios.get(fullUrl, { responseType: 'blob' });
        const blob = new Blob([response.data], { type: document.mime_type || 'application/pdf' });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
      } catch (err) {
        toast.error('Failed to open document');
      }
    }
  };

  const handleBack = () => {
    const returnUrl = searchParams.get('return');
    if (returnUrl) {
      navigate(returnUrl);
    } else {
      navigate(`/cases/${caseId}`);
    }
  };

  const getMimeCategory = (mimeType) => {
    if (!mimeType) return 'unknown';
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType === 'application/pdf') return 'pdf';
    if (mimeType.includes('word') || mimeType.includes('document')) return 'word';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'excel';
    return 'other';
  };

  // PDF Document callbacks
  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    setNumPages(numPages);
    setPageNumber(1);
    setPdfError(null);
  }, []);

  const onDocumentLoadError = useCallback((error) => {
    console.error('PDF load error:', error);
    setPdfError(error.message || 'Failed to load PDF');
  }, []);

  const goToPrevPage = () => {
    setPageNumber(prev => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setPageNumber(prev => Math.min(prev + 1, numPages || 1));
  };

  const zoomIn = () => {
    setScale(prev => Math.min(prev + 0.2, 3));
  };

  const zoomOut = () => {
    setScale(prev => Math.max(prev - 0.2, 0.5));
  };

  const renderPDFViewer = () => {
    if (!blobUrl) {
      return (
        <div className="flex flex-col items-center justify-center h-[60vh] text-muted-foreground">
          <FileText className="w-16 h-16 mb-4 opacity-50" />
          <p className="text-lg font-medium mb-2">PDF Document</p>
          <p className="text-sm mb-4">Click below to view or download this document.</p>
          <div className="flex gap-3">
            <Button onClick={handleOpenInNewTab}>
              <ExternalLink className="w-4 h-4 mr-2" />
              Open PDF
            </Button>
            <Button variant="outline" onClick={handleDownload}>
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
          </div>
        </div>
      );
    }

    // If PDF failed to load with react-pdf, fall back to iframe
    if (pdfError) {
      return (
        <div className="w-full h-[75vh] bg-white rounded-lg overflow-hidden border relative">
          <iframe
            src={blobUrl}
            className="w-full h-full border-0"
            title={document?.document_name || 'Document'}
            style={{ minHeight: '600px' }}
          />
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center">
        {/* PDF Controls */}
        <div className="flex items-center gap-4 mb-4 p-3 bg-muted rounded-lg">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={goToPrevPage}
              disabled={pageNumber <= 1}
              data-testid="pdf-prev-page"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm px-2 min-w-[100px] text-center">
              Page {pageNumber} of {numPages || '...'}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={goToNextPage}
              disabled={pageNumber >= (numPages || 1)}
              data-testid="pdf-next-page"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          
          <div className="h-6 w-px bg-border" />
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={zoomOut}
              disabled={scale <= 0.5}
              data-testid="pdf-zoom-out"
            >
              <ZoomOut className="w-4 h-4" />
            </Button>
            <span className="text-sm px-2 min-w-[60px] text-center">
              {Math.round(scale * 100)}%
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={zoomIn}
              disabled={scale >= 3}
              data-testid="pdf-zoom-in"
            >
              <ZoomIn className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* PDF Document */}
        <div className="w-full overflow-auto bg-gray-100 rounded-lg p-4" style={{ maxHeight: '70vh' }}>
          <div className="flex justify-center">
            <Document
              file={blobUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={
                <div className="flex flex-col items-center justify-center py-20">
                  <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
                  <p className="text-muted-foreground">Loading PDF...</p>
                </div>
              }
              error={
                <div className="flex flex-col items-center justify-center py-20">
                  <FileText className="w-12 h-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground mb-4">Failed to render PDF</p>
                  <Button variant="outline" onClick={handleOpenInNewTab}>
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Open in Browser
                  </Button>
                </div>
              }
            >
              <Page
                pageNumber={pageNumber}
                scale={scale}
                renderTextLayer={true}
                renderAnnotationLayer={true}
                loading={
                  <div className="flex items-center justify-center py-20">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  </div>
                }
                className="shadow-lg"
              />
            </Document>
          </div>
        </div>
      </div>
    );
  };

  const renderDocumentViewer = () => {
    if (loadingContent) {
      return (
        <div className="flex flex-col items-center justify-center h-[60vh] text-muted-foreground">
          <Loader2 className="w-12 h-12 mb-4 animate-spin text-primary" />
          <p>Loading document...</p>
        </div>
      );
    }

    if (!blobUrl) {
      return (
        <div className="flex flex-col items-center justify-center h-[60vh] text-muted-foreground">
          <File className="w-16 h-16 mb-4 opacity-50" />
          <p className="text-lg font-medium mb-2">Unable to preview document</p>
          <p className="text-sm mb-4">Click below to download the file.</p>
          <Button onClick={handleDownload}>
            <Download className="w-4 h-4 mr-2" />
            Download Document
          </Button>
        </div>
      );
    }

    const mimeCategory = getMimeCategory(document?.mime_type);

    switch (mimeCategory) {
      case 'pdf':
        return renderPDFViewer();
      
      case 'image':
        return (
          <div className="flex items-center justify-center bg-muted/30 rounded-lg p-4">
            <img 
              src={blobUrl} 
              alt={document?.document_name || 'Document'} 
              className="max-w-full max-h-[70vh] object-contain rounded shadow-lg"
            />
          </div>
        );
      
      case 'word':
      case 'excel':
        // For Office documents, offer download
        return (
          <div className="flex flex-col items-center justify-center h-[60vh] text-muted-foreground">
            <FileText className="w-16 h-16 mb-4 opacity-50" />
            <p className="text-lg font-medium mb-2">Preview not available for this file type</p>
            <p className="text-sm mb-4">This document type cannot be previewed in the browser.</p>
            <Button onClick={handleDownload}>
              <Download className="w-4 h-4 mr-2" />
              Download to View
            </Button>
          </div>
        );
      
      default:
        // For unknown types, try to display or offer download
        return (
          <div className="flex flex-col items-center justify-center h-[60vh] text-muted-foreground">
            <File className="w-16 h-16 mb-4 opacity-50" />
            <p className="text-lg font-medium mb-2">Preview not available</p>
            <p className="text-sm mb-4">Click below to download and view this document.</p>
            <Button onClick={handleDownload}>
              <Download className="w-4 h-4 mr-2" />
              Download Document
            </Button>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[80vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={handleBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Case
        </Button>
        <Card className="card-shadow">
          <CardContent className="py-12 text-center">
            <FileText className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-medium mb-2">Document Not Found</h3>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="document-viewer-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={handleBack} data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Case
          </Button>
          <div>
            <h1 className="text-xl font-semibold">{document?.document_name || 'Document'}</h1>
            <div className="flex items-center gap-2 mt-1">
              {document?.mime_type && (
                <Badge variant="secondary" className="text-xs">
                  {getMimeCategory(document.mime_type).toUpperCase()}
                </Badge>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {blobUrl && (
            <Button variant="outline" onClick={handleOpenInNewTab} data-testid="open-new-tab-button">
              <ExternalLink className="w-4 h-4 mr-2" />
              Open in New Tab
            </Button>
          )}
          <Button onClick={handleDownload} data-testid="download-button">
            <Download className="w-4 h-4 mr-2" />
            Download
          </Button>
        </div>
      </div>

      {/* Document Viewer */}
      <Card className="card-shadow">
        <CardContent className="p-4">
          {renderDocumentViewer()}
        </CardContent>
      </Card>
    </div>
  );
};

export default DocumentViewerPage;
